import * as React from 'react';
import { useLingui } from '@lingui/react/macro';
import { classNames } from 'utils/ui/css';
import { uiState } from 'state/ui';

export type PhotoSource = 'render' | 'real';

export interface PhotoSourceToggleProps {
  /**
   * The currently active photo source
   */
  currentSource: PhotoSource;
  /**
   * URL of the preview image to show in the toggle button (usually the first image from the alternate source)
   */
  previewImageUrl: string;
  /**
   * Callback when the toggle is clicked
   */
  onToggle: () => void;
  /**
   * Additional CSS class names
   */
  className?: string;
}

/**
 * A toggle button that allows switching between rendered photos and real dealership photos.
 * Shows a thumbnail preview of the alternate photo source with a tooltip.
 */
const PhotoSourceToggle: React.FC<PhotoSourceToggleProps> = ({
  currentSource,
  previewImageUrl,
  onToggle,
  className,
}) => {
  const { t } = useLingui();
  const [showTooltip, setShowTooltip] = React.useState(false);
  const { vehicleFormCollapsed } = uiState().useState();

  // Don't render if no preview image is available
  if (!previewImageUrl) {
    return null;
  }

  const tooltipText = currentSource === 'render' ? t`To dealership photos` : t`To rendered photos`;

  const ariaLabel = currentSource === 'render' ? t`Switch to dealership photos` : t`Switch to rendered photos`;

  // Position toggle below burger menu when form is collapsed, or below expanded form
  const positionClass = vehicleFormCollapsed ? 'top-32' : 'top-56';

  return (
    <div data-testid="photo-source-toggle" className={classNames('absolute left-8 z-10', positionClass, className)}>
      {showTooltip && (
        <div
          role="tooltip"
          className="absolute left-full top-0 ml-2 whitespace-nowrap rounded bg-black/80 px-2 py-1 text-sm text-white"
        >
          {tooltipText}
        </div>
      )}
      <button
        data-testid="photo-source-toggle-button"
        data-source={currentSource}
        type="button"
        onClick={onToggle}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        aria-label={ariaLabel}
        className="relative size-16 overflow-hidden rounded-lg bg-black/50 shadow-lg"
      >
        <img
          data-testid="photo-source-toggle-thumbnail"
          src={previewImageUrl}
          alt=""
          className="size-full object-cover transition-transform hover:scale-110"
        />
      </button>
    </div>
  );
};

export default PhotoSourceToggle;
